import React, { useState, useEffect } from 'react';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Divider from '@material-ui/core/Divider';
import Snackbar from '@material-ui/core/Snackbar';
import CircularProgress from '@material-ui/core/CircularProgress';
import PrintIcon from '@material-ui/icons/Print';
import SendIcon from '@material-ui/icons/Send';
import WarningIcon from '@material-ui/icons/Warning';
import { makeStyles } from '@material-ui/core/styles';
import { surgeryService } from '../../../services/surgeryService';
import { FEE_MODEL } from '../shared/constants';

const FEE_MODEL_LABELS = {
  [FEE_MODEL.PERCENTAGE_SPLIT]: 'Percentage Split',
  [FEE_MODEL.FLAT_RATE]: 'Flat Rate',
  [FEE_MODEL.DIRECT_BILLING]: 'Direct Billing',
};

const CATEGORY_ORDER = [
  'Theatre fee',
  'Surgeon fee',
  'Anaesthesia fee',
  'Consumables (estimated)',
  'Ward stay',
];

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 22,
    padding: 20,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  title: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#333',
  },
  estimateId: {
    fontSize: '0.85rem',
    color: '#777',
    fontWeight: 500,
    backgroundColor: '#F5F5F5',
    padding: '4px 12px',
    borderRadius: 4,
  },
  alertBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#FFF8E1',
    border: '1px solid #FFE082',
    borderRadius: 6,
    padding: '12px 16px',
    marginBottom: 20,
  },
  alertIcon: {
    color: '#F57F17',
    fontSize: '1.3rem',
  },
  alertText: {
    fontSize: '0.85rem',
    color: '#795548',
    fontWeight: 500,
  },
  table: {
    marginBottom: 20,
  },
  tableHead: {
    backgroundColor: '#F5F5F5',
  },
  headCell: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#555',
  },
  bodyCell: {
    fontSize: '0.85rem',
    color: '#333',
  },
  amountCell: {
    fontSize: '0.85rem',
    color: '#333',
    textAlign: 'right',
    fontFamily: 'monospace',
  },
  subtotalRow: {
    backgroundColor: '#FAFAFA',
  },
  subtotalCell: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#555',
  },
  totalRow: {
    backgroundColor: '#E8F5E9',
  },
  totalCell: {
    fontWeight: 700,
    fontSize: '1rem',
    color: '#2E7D32',
  },
  sectionTitle: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#333',
    marginTop: 24,
    marginBottom: 12,
  },
  feeSplitPaper: {
    padding: '16px 20px',
    marginBottom: 16,
    borderRadius: 8,
    border: '1px solid #E0E0E0',
  },
  detailRow: {
    display: 'flex',
    padding: '6px 0',
  },
  detailLabel: {
    width: 200,
    fontSize: '0.85rem',
    color: '#777',
    fontWeight: 500,
    flexShrink: 0,
  },
  detailValue: {
    fontSize: '0.85rem',
    color: '#333',
    flex: 1,
  },
  actions: {
    display: 'flex',
    gap: 12,
    marginTop: 20,
  },
  printBtn: {
    textTransform: 'none',
    fontWeight: 500,
  },
  sendBtn: {
    textTransform: 'none',
    fontWeight: 500,
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: 80,
  },
  errorText: {
    textAlign: 'center',
    padding: 40,
    color: '#F44336',
  },
}));

const SurgeryCostEstimate = ({ surgeryId }) => {
  const classes = useStyles();

  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  useEffect(() => {
    let cancelled = false;
    const fetchEstimate = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await surgeryService.generateEstimate({ surgery_id: surgeryId });
        const data = res?.data || res;
        if (!cancelled) setEstimate(data);
      } catch (err) {
        console.error('Failed to generate estimate:', err);
        if (!cancelled) setError('Failed to load cost estimate. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchEstimate();
    return () => { cancelled = true; };
  }, [surgeryId]);

  const handlePrint = () => {
    setSnackbar({ open: true, message: 'Preparing estimate for printing...' });
  };

  const handleSendToPatient = () => {
    setSnackbar({ open: true, message: 'Estimate sent to patient successfully.' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '' });
  };

  if (loading) {
    return (
      <Paper className={classes.root} elevation={1}>
        <div className={classes.loadingWrapper}>
          <CircularProgress size={32} />
        </div>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper className={classes.root} elevation={1}>
        <Typography className={classes.errorText}>{error}</Typography>
      </Paper>
    );
  }

  const items = estimate?.items || [];
  const totalEstimate = estimate?.total_estimate || 0;
  const currency = estimate?.currency || 'UGX';
  const estimateId = estimate?.estimate_id || 'N/A';

  // Sort items by category order
  const sortedItems = [...items].sort((a, b) => {
    const idxA = CATEGORY_ORDER.indexOf(a.description);
    const idxB = CATEGORY_ORDER.indexOf(b.description);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  });

  const subtotal = sortedItems.reduce((sum, item) => sum + (item.amount || 0), 0);

  // Mock external doctor fee split data
  const externalDoctorFee = {
    hasSplit: true,
    model: FEE_MODEL.PERCENTAGE_SPLIT,
    surgeonFee: sortedItems.find((i) => i.description === 'Surgeon fee')?.amount || 0,
    doctorPercentage: 70,
    hospitalPercentage: 30,
  };

  const doctorShare = Math.round(externalDoctorFee.surgeonFee * externalDoctorFee.doctorPercentage / 100);
  const hospitalShare = externalDoctorFee.surgeonFee - doctorShare;

  return (
    <Paper className={classes.root} elevation={1}>
      {/* Header */}
      <div className={classes.header}>
        <Typography className={classes.title}>Surgery Cost Estimate</Typography>
        <Typography className={classes.estimateId}>Estimate #{estimateId}</Typography>
      </div>

      {/* Alert Banner */}
      <div className={classes.alertBanner}>
        <WarningIcon className={classes.alertIcon} />
        <Typography className={classes.alertText}>
          This is an estimate. Final charges may vary based on actual consumables and theatre time.
        </Typography>
      </div>

      {/* Itemized Table */}
      <Table className={classes.table} size="small">
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell className={classes.headCell}>Description</TableCell>
            <TableCell className={classes.headCell} align="right">
              Amount ({currency})
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedItems.map((item, idx) => (
            <TableRow key={idx}>
              <TableCell className={classes.bodyCell}>{item.description}</TableCell>
              <TableCell className={classes.amountCell}>
                {(item.amount || 0).toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
          <TableRow className={classes.subtotalRow}>
            <TableCell className={classes.subtotalCell}>Subtotal</TableCell>
            <TableCell className={classes.subtotalCell} align="right" style={{ fontFamily: 'monospace' }}>
              {subtotal.toLocaleString()}
            </TableCell>
          </TableRow>
          <TableRow className={classes.totalRow}>
            <TableCell className={classes.totalCell}>Total Estimate</TableCell>
            <TableCell className={classes.totalCell} align="right" style={{ fontFamily: 'monospace' }}>
              {currency} {totalEstimate.toLocaleString()}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {/* External Doctor Fee Split Section */}
      {externalDoctorFee.hasSplit && externalDoctorFee.surgeonFee > 0 && (
        <>
          <Typography className={classes.sectionTitle}>External Doctor Fee Split</Typography>
          <div className={classes.feeSplitPaper}>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Fee Model</Typography>
              <Typography className={classes.detailValue}>
                {FEE_MODEL_LABELS[externalDoctorFee.model] || externalDoctorFee.model}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Surgeon Fee Collected</Typography>
              <Typography className={classes.detailValue} style={{ fontFamily: 'monospace' }}>
                {currency} {externalDoctorFee.surgeonFee.toLocaleString()}
              </Typography>
            </div>
            <Divider style={{ margin: '8px 0' }} />
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>
                Doctor's Share ({externalDoctorFee.doctorPercentage}%)
              </Typography>
              <Typography className={classes.detailValue} style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                {currency} {doctorShare.toLocaleString()}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>
                Hospital's Share ({externalDoctorFee.hospitalPercentage}%)
              </Typography>
              <Typography className={classes.detailValue} style={{ fontFamily: 'monospace' }}>
                {currency} {hospitalShare.toLocaleString()}
              </Typography>
            </div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className={classes.actions}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<PrintIcon />}
          className={classes.printBtn}
          onClick={handlePrint}
        >
          Print Estimate
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          className={classes.sendBtn}
          onClick={handleSendToPatient}
        >
          Send to Patient
        </Button>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default SurgeryCostEstimate;
